import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Wallet, Flame, Gift, Banknote } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { k: "seva", l: "Offer as Seva", d: "Donated to the temple straight away", icon: Flame },
  { k: "prasad", l: "Prasad Box credit", d: "We ship a ritual kit to you", icon: Gift },
  { k: "cash", l: "Cash payout", d: "UPI, PayPal or bank, approved by our team", icon: Banknote },
];

export default function WalletCard() {
  const [w, setW] = useState(null);
  const [hist, setHist] = useState([]);
  const load = () => {
    api.get("/ads/wallet").then((r) => setW(r.data));
    api.get("/wallet/redemptions").then((r) => setHist(r.data.items));
  };
  useEffect(load, []);
  if (!w) return null;
  const can = w.balance >= 5;

  return (
    <div data-testid="wallet-card" className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
          <div>
            <div className="overline">Ad-view wallet</div>
            <div data-testid="wallet-balance" className="font-mono text-3xl text-[#4A0E17]">${w.balance.toFixed(4)}</div>
            <div className="text-xs text-[#63534E]">Lifetime ${w.lifetime_earned.toFixed(4)} · {w.views_today}/{w.daily_cap} views today</div>
          </div>
        </div>
        <RedeemDialog balance={w.balance} disabled={!can} onDone={load} />
      </div>
      {!can && <div className="mt-4 h-2 bg-[#F3E5AB]/50 rounded-full overflow-hidden"><div className="h-full bg-[#E65100]" style={{ width: `${Math.min(100, (w.balance / 5) * 100)}%` }} /></div>}
      {!can && <div className="text-xs text-[#63534E] mt-2">You can redeem once your balance reaches $5.00.</div>}
      {hist.length > 0 && (
        <div className="mt-5 border-t border-[#D4AF37]/30 pt-4 space-y-2" data-testid="wallet-history">
          {hist.slice(0, 5).map((h) => (
            <div key={h.id} className="flex justify-between text-sm"><span className="capitalize">{h.kind} · ${h.amount.toFixed(2)}</span><span className="text-xs capitalize text-[#8C6D3B]">{h.status}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function RedeemDialog({ balance, disabled, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "seva", amount: "", shipping_address: "", payout_method: "upi", payout_details: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/redeem", { ...f, amount: parseFloat(f.amount || balance.toFixed(2)) });
      toast.success(f.kind === "seva" ? "Seva offered. Thank you!" : "Request sent. Our team will process it soon.");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="wallet-redeem-btn" disabled={disabled} className="bg-[#E65100] hover:bg-[#BF360C] rounded-full h-11 px-6">Redeem</Button></DialogTrigger>
      <DialogContent className="bg-[#FFFDF9] border-[#D4AF37]/40 max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-xl text-[#4A0E17]">Redeem your wallet</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {KINDS.map(({ k, l, d, icon: I }) => (
            <button key={k} data-testid={`redeem-kind-${k}`} onClick={() => setF({ ...f, kind: k })} className={`w-full text-left flex gap-3 items-center p-3 rounded-xl border transition-sacred ${f.kind === k ? "border-[#4A0E17] bg-[#F3E5AB]/40" : "border-[#D4AF37]/40"}`}>
              <I className="w-5 h-5 text-[#E65100]" /><div><div className="font-semibold text-[#4A0E17] text-sm">{l}</div><div className="text-xs text-[#63534E]">{d}</div></div>
            </button>
          ))}
        </div>
        <Input data-testid="redeem-amount" type="number" step="0.01" min="5" max={balance} placeholder={`Amount (max $${balance.toFixed(2)})`} value={f.amount} onChange={set("amount")} className="bg-white border-[#D4AF37]/40 h-11" />
        {f.kind === "prasad" && <Textarea data-testid="redeem-address" rows={3} placeholder="Shipping address" value={f.shipping_address} onChange={set("shipping_address")} className="bg-white border-[#D4AF37]/40" />}
        {f.kind === "cash" && (
          <div className="grid grid-cols-3 gap-2">
            <select data-testid="redeem-method" value={f.payout_method} onChange={set("payout_method")} className="h-11 rounded-md border border-[#D4AF37]/40 bg-white px-2 text-sm"><option value="upi">UPI</option><option value="paypal">PayPal</option><option value="bank">Bank</option></select>
            <Input data-testid="redeem-details" placeholder="UPI ID / email / account" value={f.payout_details} onChange={set("payout_details")} className="col-span-2 bg-white border-[#D4AF37]/40 h-11" />
          </div>
        )}
        <Button data-testid="redeem-submit" disabled={busy} onClick={submit} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11">{busy ? "Processing…" : "Confirm"}</Button>
      </DialogContent>
    </Dialog>
  );
}
